import React, { useEffect, useMemo, useState } from "react";
import { MdGavel, MdPolicy } from "react-icons/md";
import { FiClock, FiCheckCircle, FiAlertCircle, FiPrinter } from "react-icons/fi";
import { message } from "antd";
import Load from "../Load/Load";
import { getPublicContents } from "../../Web Apis/webApis";

const CONTENT_CONFIG = {
  "terms-and-conditions": {
    title: "Terms & Conditions",
    badge: "Legal",
    icon: MdGavel,
    gradient: "from-emerald-500 via-teal-500 to-cyan-600",
  },
  "privacy-policy": {
    title: "Privacy Policy",
    badge: "Privacy",
    icon: MdPolicy,
    gradient: "from-indigo-500 via-purple-500 to-fuchsia-600",
  },
};

const escapeHtml = (value) => {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

const buildHtmlFromText = (raw = "") => {
  const lines = raw.split("\n");
  let html = "";
  let inList = false;

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      if (inList) {
        html += "</ul>";
        inList = false;
      }
      html += '<div class="policy-spacer"></div>';
      return;
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      if (inList) {
        html += "</ul>";
        inList = false;
      }
      html += `<h3>${escapeHtml(trimmed)}</h3>`;
      return;
    }

    if (/^[•*-]\s+/.test(trimmed)) {
      if (!inList) {
        html += "<ul>";
        inList = true;
      }
      html += `<li>${escapeHtml(trimmed.replace(/^[•*-]\s+/, ""))}</li>`;
      return;
    }

    if (inList) {
      html += "</ul>";
      inList = false;
    }
    html += `<p>${escapeHtml(trimmed)}</p>`;
  });

  if (inList) {
    html += "</ul>";
  }

  return html;
};

const formatDate = (value) => {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const PolicyPage = ({ slug }) => {
  const config = CONTENT_CONFIG[slug] || CONTENT_CONFIG["privacy-policy"];
  const Icon = config.icon;
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const response = await getPublicContents(true);
      if (response?.data?.success) {
        const match = response?.data?.data?.find((item) => item.slug === slug);
        setContent(match || null);
      } else {
        message.error("Unable to load content");
      }
    } catch (error) {
      console.error("Error fetching public content:", error);
      message.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, [slug]);

  useEffect(() => {
    if (content?.title) {
      document.title = `${content.title} | Spoki`;
    }
  }, [content]);

  const htmlContent = useMemo(() => {
    if (!content?.content) return "";
    if (/<[a-z][\s\S]*>/i.test(content.content)) {
      return content.content;
    }
    return buildHtmlFromText(content.content);
  }, [content]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div
        className={`relative overflow-hidden bg-gradient-to-r ${config.gradient} text-white`}
      >
        <div className="absolute inset-0 opacity-20">
          <div className="absolute -top-10 -left-10 h-40 w-40 rounded-full bg-white/30 blur-2xl" />
          <div className="absolute bottom-0 right-0 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        </div>
        <div className="relative mx-auto flex max-w-6xl flex-col gap-4 px-6 py-16 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20 text-3xl">
              <Icon />
            </div>
            <div>
              <span className="text-sm uppercase tracking-widest text-white/80">
                {config.badge}
              </span>
              <h1 className="text-3xl font-semibold md:text-4xl">
                {content?.title || config.title}
              </h1>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="rounded-full bg-white/15 px-4 py-2 text-sm">
              Updated: {formatDate(content?.updatedAt)}
            </div>
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-2 text-sm transition hover:bg-white/30"
            >
              <FiPrinter />
              Print
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-6xl gap-8 px-6 py-10 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-6">
          <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-xl md:p-10">
            {loading ? (
              <div className="py-10">
                <Load />
              </div>
            ) : content ? (
              <div
                className="policy-content"
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />
            ) : (
              <div className="flex flex-col items-center justify-center gap-4 py-12 text-center">
                <FiAlertCircle className="text-4xl text-red-500" />
                <p className="text-lg font-semibold text-slate-700">
                  Content not available
                </p>
                <p className="max-w-md text-sm text-slate-500">
                  We could not find the requested policy. Please check back later.
                </p>
              </div>
            )}
          </div>

          <div className="rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-6 py-8 text-white shadow-lg">
            <h2 className="text-xl font-semibold">Need help?</h2>
            <p className="mt-2 text-sm text-slate-200">
              If you have questions about these policies, please contact our support team.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <span className="rounded-full bg-white/10 px-4 py-2 text-sm">
                Email: spokillp@gmail.com
              </span>
              <span className="rounded-full bg-white/10 px-4 py-2 text-sm">
                Response within 24-48 hours
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-6 policy-side-stack">
          <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-slate-800">Policy summary</h3>
            <div className="mt-4 space-y-4 text-sm text-slate-600">
              <div className="flex items-center gap-3">
                <FiClock className="text-lg text-teal-500" />
                <div>
                  <p className="font-medium text-slate-700">Last updated</p>
                  <p>{formatDate(content?.updatedAt)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <FiCheckCircle className="text-lg text-emerald-500" />
                <div>
                  <p className="font-medium text-slate-700">Status</p>
                  <p>{content?.isPublished ? "Published" : "Unpublished"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Icon className="text-lg text-indigo-500" />
                <div>
                  <p className="font-medium text-slate-700">Category</p>
                  <p>{config.badge}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-slate-800">Policy owner</h3>
            <div className="mt-4 space-y-2 text-sm text-slate-600">
              <p className="font-medium text-slate-700">
                {content?.updatedBy?.name || "Spoki Team"}
              </p>
              <p>{content?.updatedBy?.email || "spokillp@gmail.com"}</p>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-slate-800">Highlights</h3>
            <ul className="mt-4 space-y-3 text-sm text-slate-600">
              <li className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 rounded-full bg-teal-500" />
                Always review updates before using the service.
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 rounded-full bg-indigo-500" />
                Your data is processed under applicable laws.
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 rounded-full bg-emerald-500" />
                Contact support for clarifications anytime.
              </li>
            </ul>
          </div>

          <div className="rounded-3xl border border-dashed border-slate-200 bg-white/60 p-6 text-sm text-slate-500">
            This policy applies to all users of the Spoki app and related services.
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyPage;

